import { DateTime } from 'luxon'
import { DEFAULT_TIMEZONE_NAME } from '@super/shared-types'

const DEFAULT_REPORTING_TIMEZONE = DEFAULT_TIMEZONE_NAME

function isValidIanaTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezone })
    return true
  } catch {
    return false
  }
}

function normalizeTimezone(timezoneName: string): string {
  if (timezoneName.includes('/')) {
    return timezoneName
  }

  if (!timezoneName.includes('_')) {
    return timezoneName
  }

  const [region, ...nameParts] = timezoneName.toLowerCase().split('_')
  const capitalize = (value: string) =>
    value.charAt(0).toUpperCase() + value.slice(1)

  if (!region || nameParts.length === 0) {
    return timezoneName
  }

  const regionPrefix = `${capitalize(region)}/`

  const generateGroupings = (parts: string[]): string[][] => {
    if (parts.length === 0) {
      return [[]]
    }

    const groupings: string[][] = []
    for (let groupSize = 1; groupSize <= parts.length; groupSize += 1) {
      const currentGroup = parts.slice(0, groupSize).map(capitalize).join('_')
      const remaining = parts.slice(groupSize)
      for (const grouping of generateGroupings(remaining)) {
        groupings.push([currentGroup, ...grouping])
      }
    }
    return groupings
  }

  const groupedCandidates = generateGroupings(nameParts)
    .map(groups => `${regionPrefix}${groups.join('/')}`)
    .sort((a, b) => b.length - a.length)

  const validCandidate = groupedCandidates.find(candidate =>
    isValidIanaTimezone(candidate),
  )

  return validCandidate ?? timezoneName
}

export function resolveReportingTimezone(timezoneName?: string): string {
  if (!timezoneName) {
    return DEFAULT_REPORTING_TIMEZONE
  }

  const candidate = normalizeTimezone(timezoneName)

  return isValidIanaTimezone(candidate) ? candidate : DEFAULT_REPORTING_TIMEZONE
}

function toIsoStringOrFallback(dateTime: DateTime, fallbackDate: Date): string {
  return dateTime.toUTC().toISO() ?? fallbackDate.toISOString()
}

export function getReportingDateParams(
  dateRange: string,
  timezoneName?: string,
  customDates?: { start: string; end: string } | null,
  referenceNow?: DateTime,
): { startDate: string; endDate: string } {
  const timezone = resolveReportingTimezone(timezoneName)
  const now = (referenceNow ?? DateTime.now()).setZone(timezone)

  if (dateRange === 'custom' && customDates?.start && customDates?.end) {
    const customStart = DateTime.fromISO(customDates.start, {
      zone: timezone,
    }).startOf('day')
    const customEnd = DateTime.fromISO(customDates.end, {
      zone: timezone,
    }).endOf('day')

    return {
      startDate: toIsoStringOrFallback(
        customStart,
        new Date(customDates.start),
      ),
      endDate: toIsoStringOrFallback(customEnd, new Date(customDates.end)),
    }
  }

  const daysByRange: Record<string, number> = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
  }
  const daysToSubtract = daysByRange[dateRange] ?? 7
  const start = now.minus({ days: daysToSubtract })

  return {
    startDate: toIsoStringOrFallback(
      start,
      new Date(Date.now() - 7 * 86400000),
    ),
    endDate: toIsoStringOrFallback(now, new Date()),
  }
}
